import { useForm, SubmitHandler } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoginMutation } from '../api/authApiSlice';
import { setCredentials } from '../authSlice';
import { Credentials } from '../types';
import { useEffect } from 'react';

export const LoginForm = () => {
  const { register, handleSubmit, formState: { errors }, setError } = useForm<Credentials>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener la ubicación de redirección (si existe)
  const from = location.state?.from?.pathname || '/';

  // Hook de mutación RTK Query con optimizado de tipos de error
  const [login, { isLoading, error, isSuccess, data: loginData }] = useLoginMutation();

  // Manejar el envío del formulario
  const onSubmit: SubmitHandler<Credentials> = async (data) => {
    try {
      // Llamar a la mutación de login
      await login(data).unwrap(); // unwrap() propaga el error si falla, que lo capturaremos en el catch
    } catch (err: any) {
      // Manejar diferentes tipos de errores
      if (err.status === 401) {
        // Error de credenciales
        setError('root.serverError', {
          type: 'auth',
          message: 'Credenciales incorrectas. Por favor, verifica tu email y contraseña.',
        });
      } else if (err.status === 400) {
        // Error de validación
        if (err.data?.errors) {
          // Si la API devuelve errores específicos por campo
          Object.entries(err.data.errors).forEach(([field, message]) => {
            if (field === 'email' || field === 'password') {
              setError(field as keyof Credentials, {
                type: 'server',
                message: message as string,
              });
            }
          });
        } else {
          setError('root.serverError', {
            type: 'validation',
            message: err.data?.message || 'Error de validación',
          });
        }
      } else {
        // Otros errores
        setError('root.serverError', {
          type: 'server',
          message: err.data?.message || 'Error al iniciar sesión. Por favor, intenta de nuevo más tarde.',
        });
      }
      console.error('Error en inicio de sesión:', err);
    }
  };

  // Efecto para manejar el éxito del login
  useEffect(() => {
    if (isSuccess && loginData) {
      // Guardar credenciales en Redux y localStorage
      dispatch(setCredentials(loginData));
      
      // Redirigir al usuario a la página desde donde vino o al dashboard
      navigate(from, { replace: true });
    }
  }, [isSuccess, loginData, dispatch, navigate, from]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 text-center">
      {/* Campo Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white text-center">
          Correo Electrónico
        </label>
        <div className="mt-2">
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register("email", { 
              required: "El correo es obligatorio",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Formato de email no válido"
              }
            })}
            className={`block w-full rounded-md border-0 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset text-center ${errors.email ? 'ring-red-300 dark:ring-red-700 focus:ring-red-500 dark:focus:ring-red-500' : 'ring-gray-300 dark:ring-gray-600 focus:ring-indigo-600 dark:focus:ring-indigo-500'} placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
          />
          {errors.email && <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">{errors.email.message}</p>}
        </div>
      </div>

      {/* Campo Contraseña */}
      <div>
        <div className="flex items-center justify-center">
          <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white text-center">
            Contraseña
          </label>
        </div>
        <div className="mt-2">
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            {...register("password", { 
              required: "La contraseña es obligatoria",
              minLength: {
                value: 6,
                message: "La contraseña debe tener al menos 6 caracteres"
              }
            })}
            className={`block w-full rounded-md border-0 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset text-center ${errors.password ? 'ring-red-300 dark:ring-red-700 focus:ring-red-500 dark:focus:ring-red-500' : 'ring-gray-300 dark:ring-gray-600 focus:ring-indigo-600 dark:focus:ring-indigo-500'} placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
          />
          {errors.password && <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">{errors.password.message}</p>}
        </div>
      </div>

      {/* Mostrar errores del servidor */}
      {errors.root?.serverError && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-700">
          <div className="flex justify-center">
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-400 text-center">{errors.root.serverError.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Botón de Envío */}
      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
        >
          {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
        </button>
      </div>
    </form>
  );
}; 